import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Minus, ShoppingCart } from 'lucide-react';
import api, { resolveUrl } from '../lib/api';

/**
 * Modal to customize a menu item with its modifier groups before adding to cart.
 * Props:
 *   item: the menu item object
 *   modifierGroups: array of ALL modifier groups (we filter by item.modifierGroupIds)
 *   onClose: () => void
 *   onAdd: (customizedItem) => void  where customizedItem has {id, name, price, image, modifiers}
 */
export default function ItemCustomizer({ item, modifierGroups, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [selections, setSelections] = useState({}); // { groupId: [{name, price}] }
  const [error, setError] = useState('');

  const groups = useMemo(() => {
    if (!item?.modifierGroupIds?.length) return [];
    return item.modifierGroupIds
      .map((id) => modifierGroups.find((g) => g.id === id))
      .filter(Boolean);
  }, [item, modifierGroups]);

  useEffect(() => { setSelections({}); setError(''); setQty(1); }, [item?.id]);

  const toggle = (group, option) => {
    setError('');
    setSelections((cur) => {
      const currentSel = cur[group.id] || [];
      const already = currentSel.find((o) => o.name === option.name);
      let next;
      if (already) {
        next = currentSel.filter((o) => o.name !== option.name);
      } else {
        const maxQ = group.maxQty || 0;
        if (maxQ === 1) {
          next = [option]; // radio behavior
        } else if (maxQ > 0 && currentSel.length >= maxQ) {
          return cur; // ignore
        } else {
          next = [...currentSel, option];
        }
      }
      return { ...cur, [group.id]: next };
    });
  };

  const extraPrice = Object.values(selections).flat().reduce((s, o) => s + (o.price || 0), 0);
  const linePrice = (item?.price || 0) + extraPrice;
  const total = linePrice * qty;

  const submit = () => {
    // Validate required groups
    for (const g of groups) {
      const req = g.requiredQty || 0;
      const sel = selections[g.id] || [];
      if (req > 0 && sel.length < req) {
        setError(`Please choose ${req} option${req !== 1 ? 's' : ''} from "${g.name}".`);
        return;
      }
    }
    const flatMods = Object.entries(selections).flatMap(([gid, opts]) =>
      opts.map((o) => ({ groupId: gid, groupName: (groups.find((g) => g.id === gid) || {}).name, name: o.name, price: o.price }))
    );
    const modsSummary = flatMods.map((m) => m.name).join(', ');
    // Compose display name
    const displayName = modsSummary ? `${item.name} (${modsSummary})` : item.name;
    // Add to cart, one call per qty so each qty is treated separately if user opens modal again
    for (let i = 0; i < qty; i++) {
      onAdd({
        id: `${item.id}-${btoa(unescape(encodeURIComponent(modsSummary))).slice(0, 10)}`,
        menuItemId: item.id,
        name: displayName,
        price: linePrice,
        image: item.image,
        pst: !!item.pst,
        pstAmount: item.pstAmount ?? null,
        modifiers: flatMods,
      });
    }
    onClose();
  };

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[95] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {item.image ? (
            <img src={resolveUrl(item.image)} alt={item.name} className="w-full h-48 sm:h-56 object-cover" />
          ) : (
            <div className="w-full h-32 bg-[#FDF3EC] flex items-center justify-center text-[#ED2E2E] font-display font-bold text-4xl">WRAPPUS</div>
          )}
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur text-[#0F1C2E] hover:text-[#ED2E2E] flex items-center justify-center shadow-lg">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex items-start justify-between gap-4 mb-1">
            <h2 className="font-heading font-bold text-2xl text-[#0F1C2E]">{item.name}</h2>
            <span className="text-[#ED2E2E] font-bold text-xl whitespace-nowrap">${item.price.toFixed(2)}</span>
          </div>
          {item.description && <p className="text-gray-600 text-sm mb-5">{item.description}</p>}

          {groups.map((g) => {
            const sel = selections[g.id] || [];
            const singleChoice = g.maxQty === 1;
            const isRequired = (g.requiredQty || 0) > 0;
            return (
              <div key={g.id} className="mb-6 border-t border-gray-100 pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-heading font-semibold text-[#0F1C2E]">{g.name}</h3>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {isRequired ? <span className="text-[#ED2E2E] font-semibold">Required</span> : 'Optional'}
                      {g.maxQty > 0 && ` · choose up to ${g.maxQty}`}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {g.options.map((opt) => {
                    const isSelected = !!sel.find((o) => o.name === opt.name);
                    return (
                      <label key={opt.name} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition ${isSelected ? 'border-[#ED2E2E] bg-[#FDF3EC]' : 'border-gray-100 hover:border-gray-300'}`}>
                        <div className="flex items-center gap-3">
                          <input type={singleChoice ? 'radio' : 'checkbox'} name={g.id} checked={isSelected} onChange={() => toggle(g, opt)} className="accent-[#ED2E2E] w-4 h-4" />
                          <span className="text-sm font-medium text-[#0F1C2E]">{opt.name}</span>
                        </div>
                        {opt.price > 0 && <span className="text-sm font-semibold text-[#ED2E2E]">+${opt.price.toFixed(2)}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {error && <div className="px-6 pb-2 text-red-600 text-sm">{error}</div>}

        <div className="border-t border-gray-100 p-4 flex items-center gap-3 bg-white">
          <div className="flex items-center border-2 border-gray-200 rounded-full">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:text-[#ED2E2E]"><Minus size={14} /></button>
            <span className="px-2 text-sm font-bold w-6 text-center">{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:text-[#ED2E2E]"><Plus size={14} /></button>
          </div>
          <button onClick={submit} className="btn-wrappus flex-1 justify-center text-sm">
            <ShoppingCart size={16} /> Add {qty} to cart · ${total.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}
